import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Fade,
  Zoom,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Skeleton,
  Alert,
  LinearProgress,
  InputAdornment,
} from '@mui/material';
import {
  Edit,
  History,
  Gavel,
  Add,
  Download,
  Search,
  AccountBalance,
  CreditCard,
  Refresh,
  Clear,
  Badge,
  Upload,
} from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import {
  useLazyGetBankDetailsQuery,
  useCreateBankDetailsMutation,
  useUpdateBankDetailsMutation,
  useDownloadExcelMutation,
  useGetGatewaysQuery,
} from '../services/bankApi';
import { BankFormDialog } from '../components/BankFormDialog';
import { BankHistoryDialog } from '../components/BankHistoryDialog';
import { BankBlacklistDialog } from '../components/BankBlacklistDialog';
import BlacklistManager from '../components/BlacklistManager';
import BulkSettlementDaysDialog from '../components/BulkSettlementDaysDialog';
import {
  AggregatorOption,
  BankRow,
  BlacklistEntry,
  getGatewayParam,
  mapToBankRow,
} from '../types/bankdetails';

export default function BankDetails() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [rows, setRows] = useState<BankRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [openBlacklistDialog, setOpenBlacklistDialog] = useState(false);
  const [openBlacklistManager, setOpenBlacklistManager] = useState(false);
  const [openBulkSettlement, setOpenBulkSettlement] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [selectedBankForHistory, setSelectedBankForHistory] = useState<BankRow | null>(null);
  const [selectedBankForBlacklist, setSelectedBankForBlacklist] = useState<BankRow | null>(null);
  const [selectedBlacklistEntry, setSelectedBlacklistEntry] = useState<BlacklistEntry | null>(null);

  // API Hooks
  const [triggerGetBankDetails, { isFetching }] = useLazyGetBankDetailsQuery();
  const [createBankDetails] = useCreateBankDetailsMutation();
  const [updateBankDetails] = useUpdateBankDetailsMutation();
  const [downloadExcel] = useDownloadExcelMutation();
  const {
    data: gatewaysData,
    isLoading: gatewaysLoading,
    error: gatewaysError,
    refetch: refetchGateways,
  } = useGetGatewaysQuery();

  // Compute default gateway separately to avoid unnecessary recomputes
  const defaultGateway = useMemo(() => {
    const availableGateways = gatewaysData?.gateways || [];
    return availableGateways.length > 0 ? availableGateways[0] : 'Billdesk V1';
  }, [gatewaysData]);

  // URL Params
  const searchParamsValues = useMemo(() => {
    return {
      page: parseInt(searchParams.get('page') || '1', 10),
      pageSize: parseInt(searchParams.get('pageSize') || '20', 10),
      aggregator: (searchParams.get('aggregator') as AggregatorOption) || defaultGateway,
      ifscFilter: searchParams.get('ifsc') || '',
      merchantIdFilter: searchParams.get('merchantId') || '',
    };
  }, [searchParams, defaultGateway]);

  const { page, pageSize, aggregator, ifscFilter, merchantIdFilter } = searchParamsValues;

  const [ifscInputValue, setIfscInputValue] = useState(ifscFilter); // Separate input value from filter
  const [merchantIdInputValue, setMerchantIdInputValue] = useState(merchantIdFilter);
  const showSettlement = Boolean(merchantIdFilter.trim());

  // Extracted refreshData utility - reusable fetch + map + setState logic
  const refreshData = useCallback(async () => {
    const res = await triggerGetBankDetails({
      gateway: getGatewayParam(aggregator),
      ifsc: ifscFilter.trim() || undefined,
      merchantId: merchantIdFilter.trim() || undefined,
      page,
      limit: pageSize,
    });
    if ('data' in res && res.data) {
      const payload: any = res.data;
      const raw = payload.data ?? payload;
      const mapped: BankRow[] = Array.isArray(raw)
        ? raw.map((it: any, idx: number) => mapToBankRow(it, idx))
        : [];
      setRows(mapped);
      setTotalCount(payload.meta?.total ?? payload.data?.length ?? 0);
    }
  }, [triggerGetBankDetails, aggregator, ifscFilter, merchantIdFilter, page, pageSize]);

  // Update Search Params
  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === '' || value === undefined) {
            newParams.delete(key);
          } else {
            newParams.set(key, value);
          }
        });
        return newParams;
      });
    },
    [setSearchParams]
  );

  // Debounced IFSC Search
  const debouncedIfscSearch = useMemo(
    () =>
      debounce((value: string) => {
        updateSearchParams({ ifsc: value || null, page: '1' });
      }, 500),
    [updateSearchParams]
  );

  // Debounced Merchant ID Search
  const debouncedMerchantIdSearch = useMemo(
    () =>
      debounce((value: string) => {
        updateSearchParams({ merchantId: value || null, page: '1' });
      }, 500),
    [updateSearchParams]
  );

  // Cleanup debounce on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      debouncedIfscSearch.cancel();
      debouncedMerchantIdSearch.cancel();
    };
  }, [debouncedIfscSearch, debouncedMerchantIdSearch]);

  // Handle IFSC input change
  const handleIfscInputChange = useCallback(
    (value: string) => {
      setIfscInputValue(value); // Update input value immediately for UI
      debouncedIfscSearch(value); // Debounce the actual API call
    },
    [debouncedIfscSearch]
  );

  // Handle Merchant ID input change
  const handleMerchantIdInputChange = useCallback(
    (value: string) => {
      setMerchantIdInputValue(value);
      debouncedMerchantIdSearch(value);
    },
    [debouncedMerchantIdSearch]
  );

  // Sync input values with URL params
  useEffect(() => {
    setIfscInputValue(ifscFilter);
  }, [ifscFilter]);

  useEffect(() => {
    setMerchantIdInputValue(merchantIdFilter);
  }, [merchantIdFilter]);

  // Handlers
  const handlePageChange = useCallback(
    (newPage: number) => {
      updateSearchParams({ page: newPage.toString() });
    },
    [updateSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      updateSearchParams({ pageSize: newPageSize.toString(), page: '1' });
    },
    [updateSearchParams]
  );

  const handleAggregatorChange = useCallback(
    (newAggregator: AggregatorOption) => {
      updateSearchParams({ aggregator: newAggregator, page: '1' });
    },
    [updateSearchParams]
  );

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const filteredRows = useMemo(() => rows, [rows]);

  const handleDownload = async () => {
    try {
      const gateway = getGatewayParam(aggregator);
      const blob = await downloadExcel({
        gateway,
        ifsc: ifscFilter.trim() || undefined,
        bankName: undefined,
      }).unwrap();
      const dlUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = dlUrl;
      link.setAttribute('download', 'bank-details.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      console.error('Download failed', e);
    }
  };

  const handleAddBank = async (formData: any) => {
    if (!formData.name || !formData.ifscCode) {
      return;
    }

    try {
      const trimmedMerchantId = merchantIdFilter.trim();
      const bankData: any = {
        gateway: getGatewayParam(aggregator),
        ifsc: formData.ifscCode,
        bankName: formData.name,
        remarks: formData.remarks,
        isActive: true,
        lumpsumNetBanking: !!formData.nbLumpsum,
        hybridNetBanking: !!formData.nbHybrid,
        lumpsumDebitCard: !!formData.nbDebitCard,
        emandateNetBanking: !!formData.emdNetBanking,
        emandateDebitCard: !!formData.emdDebitCard,
        emandateAadhar: !!formData.emdAadhaar,
        emandatePAN: !!formData.emdPan,
        otmNetBanking: !!formData.emdNetBanking,
        otmDebitCard: !!formData.emdDebitCard,
        lumpsumNetBankingBankId: formData.nbBankId || '',
        hybridNetBankingBankId: formData.nbHybridBankId || '',
        lumpsumDebitCardBankId: formData.nbDebitCardBankId || '',
        emandateBankId: formData.emdBankId || '',
        // Corporate bank IDs
        lumpsumNetBankingBankIdCorporate: formData.nbBankIdCorporate || '',
        hybridNetBankingBankIdCorporate: formData.nbHybridBankIdCorporate || '',
        lumpsumDebitCardBankIdCorporate: formData.nbDebitCardBankIdCorporate || '',
        emandateBankIdCorporate: formData.emdBankIdCorporate || '',
      };

      if (trimmedMerchantId) {
        // Settlement days are merchant-scoped, so only send them when a
        // merchantId is selected on the page. Empty values are skipped so
        // we don't overwrite server data with blanks.
        bankData.merchantId = trimmedMerchantId;
        const settlementMap: Array<[string, string]> = [
          ['lumpsumNetBankingSettlementDays', formData.nbLumpsumSettlement],
          ['hybridNetBankingSettlementDays', formData.nbHybridSettlement],
          ['lumpsumDebitCardSettlementDays', formData.nbDebitCardSettlement],
          ['emandateNetBankingSettlementDays', formData.emdNetBankingSettlement],
          ['emandateDebitCardSettlementDays', formData.emdDebitCardSettlement],
          ['emandateAadharSettlementDays', formData.emdAadhaarSettlement],
          ['emandatePANSettlementDays', formData.emdPanSettlement],
        ];
        settlementMap.forEach(([key, value]) => {
          if (value) bankData[key] = value;
        });
      }

      await createBankDetails(bankData).unwrap();
      setOpenAddDialog(false);
      // Refresh the data
      await refreshData();
    } catch (error) {
      console.error('Failed to create bank:', error);
    }
  };

  const handleUpdateBank = async (formData: any) => {
    if (!formData.name || !formData.ifscCode || editingIndex === null) {
      return;
    }

    try {
      const current = rows[editingIndex];
      const trimmedMerchantId = merchantIdFilter.trim();

      // Always include id, gateway, ifsc and remarks
      const bankData: any = {
        id: current.id.toString(),
        gateway: getGatewayParam(aggregator),
        ifsc: formData.ifscCode,
        remarks: formData.remarks,
      };
      if (trimmedMerchantId) {
        bankData.merchantId = trimmedMerchantId;
      }

      // Conditionally include only changed fields
      if (typeof formData.name !== 'undefined' && formData.name !== current.name) {
        bankData.bankName = formData.name;
      }

      // Track whether any fields in each category changed
      const lumpsumCategoryChanged =
        typeof formData.nbLumpsum !== 'undefined' &&
        Boolean(formData.nbLumpsum) !== Boolean(current.netbanking?.lumpsum);
      const lumpsumBankIdChanged =
        typeof formData.nbBankId !== 'undefined' &&
        formData.nbBankId !== (current.netbanking?.bankId ?? '');
      const hybridCategoryChanged =
        typeof formData.nbHybrid !== 'undefined' &&
        Boolean(formData.nbHybrid) !== Boolean(current.netbanking?.hybrid);
      const hybridBankIdChanged =
        typeof formData.nbHybridBankId !== 'undefined' &&
        formData.nbHybridBankId !== (current.netbanking?.hybridBankId ?? '');
      const debitCardCategoryChanged =
        typeof formData.nbDebitCard !== 'undefined' &&
        Boolean(formData.nbDebitCard) !== Boolean(current.netbanking?.lumpsumDebitCard);
      const debitCardBankIdChanged =
        typeof formData.nbDebitCardBankId !== 'undefined' &&
        formData.nbDebitCardBankId !== (current.netbanking?.lumpsumDebitCardBankId ?? '');
      const emandateCategoryChanged =
        (typeof formData.emdNetBanking !== 'undefined' &&
          Boolean(formData.emdNetBanking) !== Boolean(current.emandate?.netBanking)) ||
        (typeof formData.emdDebitCard !== 'undefined' &&
          Boolean(formData.emdDebitCard) !== Boolean(current.emandate?.debitCard)) ||
        (typeof formData.emdAadhaar !== 'undefined' &&
          Boolean(formData.emdAadhaar) !== Boolean(current.emandate?.aadhaar)) ||
        (typeof formData.emdPan !== 'undefined' &&
          Boolean(formData.emdPan) !== Boolean(current.emandate?.pan));
      const emandateBankIdChanged =
        typeof formData.emdBankId !== 'undefined' &&
        formData.emdBankId !== (current.emandate?.bankId ?? '');
      const lumpsumSettlementChanged =
        typeof formData.nbLumpsumSettlement !== 'undefined' &&
        formData.nbLumpsumSettlement !== current.netbanking?.lumpsumSettlement;
      const hybridSettlementChanged =
        typeof formData.nbHybridSettlement !== 'undefined' &&
        formData.nbHybridSettlement !== current.netbanking?.hybridSettlement;
      const debitCardSettlementChanged =
        typeof formData.nbDebitCardSettlement !== 'undefined' &&
        formData.nbDebitCardSettlement !== current.netbanking?.lumpsumDebitCardSettlement;
      const emdNbSettlementChanged =
        typeof formData.emdNetBankingSettlement !== 'undefined' &&
        formData.emdNetBankingSettlement !== current.emandate?.netBankingSettlement;
      const emdDcSettlementChanged =
        typeof formData.emdDebitCardSettlement !== 'undefined' &&
        formData.emdDebitCardSettlement !== current.emandate?.debitCardSettlement;
      const emdAdSettlementChanged =
        typeof formData.emdAadhaarSettlement !== 'undefined' &&
        formData.emdAadhaarSettlement !== current.emandate?.aadhaarSettlement;
      const emdPanSettlementChanged =
        typeof formData.emdPanSettlement !== 'undefined' &&
        formData.emdPanSettlement !== current.emandate?.panSettlement;

      // If a category changed, include its bankId mandatorily
      if (lumpsumCategoryChanged || lumpsumBankIdChanged) {
        bankData.lumpsumNetBankingBankId = formData.nbBankId ?? current.netbanking?.bankId ?? '';
      }

      if (hybridCategoryChanged || hybridBankIdChanged) {
        bankData.hybridNetBankingBankId =
          formData.nbHybridBankId ?? current.netbanking?.hybridBankId ?? '';
      }

      if (debitCardCategoryChanged || debitCardBankIdChanged) {
        bankData.lumpsumDebitCardBankId =
          formData.nbDebitCardBankId ?? current.netbanking?.lumpsumDebitCardBankId ?? '';
      }

      if (emandateCategoryChanged || emandateBankIdChanged) {
        bankData.emandateBankId = formData.emdBankId ?? current.emandate?.bankId ?? '';
      }

      // Corporate bank ID changes
      const lumpsumCorpIdChanged =
        typeof formData.nbBankIdCorporate !== 'undefined' &&
        formData.nbBankIdCorporate !== (current.netbanking?.bankIdCorporate ?? '');
      const hybridCorpIdChanged =
        typeof formData.nbHybridBankIdCorporate !== 'undefined' &&
        formData.nbHybridBankIdCorporate !== (current.netbanking?.hybridBankIdCorporate ?? '');
      const debitCardCorpIdChanged =
        typeof formData.nbDebitCardBankIdCorporate !== 'undefined' &&
        formData.nbDebitCardBankIdCorporate !== (current.netbanking?.lumpsumDebitCardBankIdCorporate ?? '');
      const emandateCorpIdChanged =
        typeof formData.emdBankIdCorporate !== 'undefined' &&
        formData.emdBankIdCorporate !== (current.emandate?.bankIdCorporate ?? '');

      if (lumpsumCorpIdChanged || lumpsumCategoryChanged || lumpsumBankIdChanged) {
        bankData.lumpsumNetBankingBankIdCorporate = formData.nbBankIdCorporate ?? '';
      }
      if (hybridCorpIdChanged || hybridCategoryChanged || hybridBankIdChanged) {
        bankData.hybridNetBankingBankIdCorporate = formData.nbHybridBankIdCorporate ?? '';
      }
      if (debitCardCorpIdChanged || debitCardCategoryChanged || debitCardBankIdChanged) {
        bankData.lumpsumDebitCardBankIdCorporate = formData.nbDebitCardBankIdCorporate ?? '';
      }
      if (emandateCorpIdChanged || emandateCategoryChanged || emandateBankIdChanged) {
        bankData.emandateBankIdCorporate = formData.emdBankIdCorporate ?? '';
      }

      // Settlement days are merchant-scoped. Skip sending them entirely when
      // no merchantId is selected. Also skip individual paymodes whose form
      // value is empty (e.g. row had no data for that paymode and user didn't
      // pick anything) so we don't overwrite server state with blanks.
      if (trimmedMerchantId) {
        const setIfPresent = (key: string, value: string | undefined) => {
          if (value) bankData[key] = value;
        };

        if (lumpsumCategoryChanged || lumpsumBankIdChanged || lumpsumSettlementChanged) {
          setIfPresent(
            'lumpsumNetBankingSettlementDays',
            formData.nbLumpsumSettlement || current.netbanking?.lumpsumSettlement
          );
        }

        if (hybridCategoryChanged || hybridBankIdChanged || hybridSettlementChanged) {
          setIfPresent(
            'hybridNetBankingSettlementDays',
            formData.nbHybridSettlement || current.netbanking?.hybridSettlement
          );
        }

        if (debitCardCategoryChanged || debitCardBankIdChanged || debitCardSettlementChanged) {
          setIfPresent(
            'lumpsumDebitCardSettlementDays',
            formData.nbDebitCardSettlement || current.netbanking?.lumpsumDebitCardSettlement
          );
        }

        if (emandateCategoryChanged || emandateBankIdChanged || emdNbSettlementChanged) {
          setIfPresent(
            'emandateNetBankingSettlementDays',
            formData.emdNetBankingSettlement || current.emandate?.netBankingSettlement
          );
        }
        if (emandateCategoryChanged || emandateBankIdChanged || emdDcSettlementChanged) {
          setIfPresent(
            'emandateDebitCardSettlementDays',
            formData.emdDebitCardSettlement || current.emandate?.debitCardSettlement
          );
        }
        if (emandateCategoryChanged || emandateBankIdChanged || emdAdSettlementChanged) {
          setIfPresent(
            'emandateAadharSettlementDays',
            formData.emdAadhaarSettlement || current.emandate?.aadhaarSettlement
          );
        }
        if (emandateCategoryChanged || emandateBankIdChanged || emdPanSettlementChanged) {
          setIfPresent(
            'emandatePANSettlementDays',
            formData.emdPanSettlement || current.emandate?.panSettlement
          );
        }
      }

      if (
        typeof formData.nbLumpsum !== 'undefined' &&
        Boolean(formData.nbLumpsum) !== Boolean(current.netbanking?.lumpsum)
      ) {
        bankData.lumpsumNetBanking = !!formData.nbLumpsum;
      }

      if (
        typeof formData.nbHybrid !== 'undefined' &&
        Boolean(formData.nbHybrid) !== Boolean(current.netbanking?.hybrid)
      ) {
        bankData.hybridNetBanking = !!formData.nbHybrid;
      }

      if (
        typeof formData.nbDebitCard !== 'undefined' &&
        Boolean(formData.nbDebitCard) !== Boolean(current.netbanking?.lumpsumDebitCard)
      ) {
        bankData.lumpsumDebitCard = !!formData.nbDebitCard;
      }

      if (
        typeof formData.emdNetBanking !== 'undefined' &&
        Boolean(formData.emdNetBanking) !== Boolean(current.emandate?.netBanking)
      ) {
        bankData.emandateNetBanking = !!formData.emdNetBanking;
      }

      if (
        typeof formData.emdDebitCard !== 'undefined' &&
        Boolean(formData.emdDebitCard) !== Boolean(current.emandate?.debitCard)
      ) {
        bankData.emandateDebitCard = !!formData.emdDebitCard;
      }

      if (
        typeof formData.emdAadhaar !== 'undefined' &&
        Boolean(formData.emdAadhaar) !== Boolean(current.emandate?.aadhaar)
      ) {
        bankData.emandateAadhar = !!formData.emdAadhaar;
      }

      if (
        typeof formData.emdPan !== 'undefined' &&
        Boolean(formData.emdPan) !== Boolean(current.emandate?.pan)
      ) {
        bankData.emandatePAN = !!formData.emdPan;
      }

      await updateBankDetails(bankData).unwrap();
      setOpenEditDialog(false);
      setEditingIndex(null);
      // Refresh the data
      await refreshData();
    } catch (error) {
      console.error('Failed to update bank:', error);
    }
  };

  const openEditDialogForBank = (row: BankRow) => {
    setEditingIndex(rows.findIndex((r) => r.id === row.id));
    setOpenEditDialog(true);
  };

  const openHistoryDialogForBank = (bank: BankRow) => {
    setSelectedBankForHistory(bank);
    setOpenHistoryDialog(true);
  };

  const openBlacklistDialogForBank = (bank: BankRow) => {
    setSelectedBankForBlacklist(bank);
    setOpenBlacklistDialog(true);
  };

  return (
    <Box sx={{ width: '100%', p: 3, bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Fade in timeout={300}>
        <Box>
          <Breadcrumbs
            separator="·"
            sx={{
              mb: 3,
              '& .MuiBreadcrumbs-separator': { mx: 1, color: theme.palette.text.secondary },
            }}
          >
            <Link
              component={RouterLink}
              to="/dashboard"
              sx={{
                color: theme.palette.primary.main,
                fontSize: theme.typography.subtitle1.fontSize,
                fontWeight: theme.typography.subtitle1.fontWeight,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Dashboard
            </Link>
            <Typography variant="subtitle1">Bank Details</Typography>
          </Breadcrumbs>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'grey.900', mb: 1 }}>
              Bank Configuration Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage payment gateway configurations, bank details, and merchant blacklists
            </Typography>
          </Box>

          <Card
            sx={{
              mb: 3,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              borderRadius: 3,
              borderTop: `3px solid ${theme.palette.primary.main}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
                sx={{ mb: 2.5 }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                    }}
                  >
                    <AccountBalance sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                      Payment Gateway
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Switch the gateway to load its bank configurations
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1.5}>
                  {!gatewaysLoading && !gatewaysError && (
                    <Chip
                      label={`Active: ${aggregator}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  )}
                  <Tooltip title="Refresh data">
                    <span>
                      <IconButton
                        color="primary"
                        onClick={() => refreshData()}
                        disabled={isFetching}
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) },
                        }}
                      >
                        {isFetching ? <CircularProgress size={18} /> : <Refresh fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>

              <Box sx={{ minHeight: 44 }}>
                {gatewaysLoading ? (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {[0, 1, 2, 3].map((i) => (
                      <Skeleton key={i} variant="rounded" width={140} height={36} sx={{ borderRadius: 999 }} />
                    ))}
                  </Stack>
                ) : gatewaysError ? (
                  <Alert
                    severity="error"
                    action={
                      <Button color="inherit" size="small" onClick={() => refetchGateways()}>
                        Retry
                      </Button>
                    }
                    sx={{ borderRadius: 2 }}
                  >
                    Failed to load gateways. Please try again.
                  </Alert>
                ) : (
                  <ToggleButtonGroup
                    exclusive
                    value={aggregator}
                    onChange={(_, v) => v && handleAggregatorChange(v as AggregatorOption)}
                    aria-label="Payment gateway"
                    sx={{
                      flexWrap: 'wrap',
                      gap: 1,
                      '& .MuiToggleButtonGroup-grouped': {
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '999px !important',
                        mx: '0 !important',
                      },
                      '& .MuiToggleButton-root': {
                        px: 2.5,
                        py: 0.75,
                        textTransform: 'none',
                        fontWeight: 500,
                        color: 'text.secondary',
                        transition: 'all 0.2s ease',
                      },
                      '& .MuiToggleButton-root:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                      },
                      '& .MuiToggleButton-root.Mui-selected': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: 'primary.main',
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                        fontWeight: 600,
                      },
                      '& .MuiToggleButton-root.Mui-selected:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.18),
                      },
                    }}
                  >
                    {gatewaysData?.gateways?.map((gateway: string) => (
                      <ToggleButton key={gateway} value={gateway}>
                        {gateway}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'visible', position: 'relative' }}>
            {isFetching && (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                }}
              />
            )}
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                      }}
                    >
                      <AccountBalance sx={{ fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                        Bank Configurations
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {totalCount} total banks for {aggregator}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={1.5}
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                  >
                    <TextField
                      size="small"
                      placeholder="e.g. BKID"
                      label="Search by IFSC"
                      value={ifscInputValue}
                      onChange={(e) => handleIfscInputChange(e.target.value.toUpperCase())}
                      helperText={
                        ifscFilter
                          ? `${filteredRows.length} of ${totalCount} match`
                          : '4-character IFSC code'
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: ifscInputValue ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => handleIfscInputChange('')}
                              aria-label="Clear search"
                              edge="end"
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                      sx={{
                        width: { xs: '100%', sm: 260 },
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        '& .MuiFormHelperText-root': { mx: 0 },
                      }}
                    />
                    <TextField
                      size="small"
                      placeholder="e.g. 991344"
                      label="Merchant ID"
                      value={merchantIdInputValue}
                      onChange={(e) => handleMerchantIdInputChange(e.target.value)}
                      helperText={
                        merchantIdFilter
                          ? 'Settlement days loaded for this merchant'
                          : 'Enter to view settlement days'
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Badge fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: merchantIdInputValue ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => handleMerchantIdInputChange('')}
                              aria-label="Clear merchant ID"
                              edge="end"
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                      sx={{
                        width: { xs: '100%', sm: 260 },
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        '& .MuiFormHelperText-root': { mx: 0 },
                      }}
                    />
                  </Stack>
                </Stack>
              </Box>

              <TableContainer sx={{ maxHeight: 600 }}>
                <Table
                  stickyHeader
                  sx={{
                    '& .MuiTableCell-root': {
                      borderColor: alpha(theme.palette.divider, 0.8),
                      py: 1.5
                    },
                    '& thead .MuiTableCell-root': {
                      bgcolor: alpha(theme.palette.grey[100], 0.8),
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'grey.700',
                      borderBottom: `2px solid ${theme.palette.divider}`,
                      verticalAlign: 'middle'
                    },
                    '& tbody .MuiTableRow:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04)
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 60 }}>S.No</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Bank Name</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>IFSC Code</TableCell>

                      {[
                        { key: 'lumpsum', label: 'Lumpsum' },
                        { key: 'hybrid', label: 'Hybrid / iSIP' },
                        { key: 'debit', label: 'Debit Card' },
                      ].map(({ key, label }) => (
                        <TableCell
                          key={key}
                          align="center"
                          sx={{
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            minWidth: 160,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 0.25,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: 'grey.600',
                              }}
                            >
                              <AccountBalance sx={{ fontSize: 12 }} />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  lineHeight: 1,
                                }}
                              >
                                Netbanking
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, fontSize: '0.8125rem' }}
                            >
                              {label}
                            </Typography>
                          </Box>
                        </TableCell>
                      ))}

                      <TableCell
                        align="center"
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          minWidth: 220,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <CreditCard fontSize="small" />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Emandate / OTM
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ minWidth: 100 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Search sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              No banks found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {ifscFilter ? `No results for "${ifscFilter}"` : 'No bank configurations available'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRows.map((row, idx) => (
                        <Zoom in timeout={300} key={row.id}>
                          <TableRow hover>
                            <TableCell sx={{ fontWeight: 500 }}>
                              {(page - 1) * pageSize + idx + 1}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title="Edit bank details">
                                  <IconButton
                                    size="small"
                                    onClick={() => openEditDialogForBank(row)}
                                    sx={{
                                      color: 'primary.main',
                                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    '&:hover': { color: 'primary.main' }
                                  }}
                                  onClick={() => openEditDialogForBank(row)}
                                >
                                  {row.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={row.ifscCode}
                                size="small"
                                variant="outlined"
                                sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                >
                                  {row.netbanking?.bankId || '-'}
                                </Typography>
                                <Chip
                                  label={row.netbanking?.lumpsum ? 'Enabled' : 'Disabled'}
                                  size="small"
                                  color={row.netbanking?.lumpsum ? 'success' : 'default'}
                                  variant={row.netbanking?.lumpsum ? 'filled' : 'outlined'}
                                  sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                                />
                                {showSettlement && row.netbanking?.lumpsumSettlement && (
                                  <Tooltip title="Settlement">
                                    <Chip
                                      label={row.netbanking.lumpsumSettlement}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        minWidth: 52,
                                        fontFamily: 'monospace',
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                >
                                  {row.netbanking?.hybridBankId || '-'}
                                </Typography>
                                <Chip
                                  label={row.netbanking?.hybrid ? 'Enabled' : 'Disabled'}
                                  size="small"
                                  color={row.netbanking?.hybrid ? 'success' : 'default'}
                                  variant={row.netbanking?.hybrid ? 'filled' : 'outlined'}
                                  sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                                />
                                {showSettlement && row.netbanking?.hybridSettlement && (
                                  <Tooltip title="Settlement">
                                    <Chip
                                      label={row.netbanking.hybridSettlement}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        minWidth: 52,
                                        fontFamily: 'monospace',
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                >
                                  {row.netbanking?.lumpsumDebitCardBankId || '-'}
                                </Typography>
                                <Chip
                                  label={row.netbanking?.lumpsumDebitCard ? 'Enabled' : 'Disabled'}
                                  size="small"
                                  color={row.netbanking?.lumpsumDebitCard ? 'success' : 'default'}
                                  variant={
                                    row.netbanking?.lumpsumDebitCard ? 'filled' : 'outlined'
                                  }
                                  sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                                />
                                {showSettlement && row.netbanking?.lumpsumDebitCardSettlement && (
                                  <Tooltip title="Settlement">
                                    <Chip
                                      label={row.netbanking.lumpsumDebitCardSettlement}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        minWidth: 52,
                                        fontFamily: 'monospace',
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                >
                                  {row.emandate?.bankId || '-'}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(4, auto)',
                                    gap: 0.5,
                                  }}
                                >
                                  <Tooltip title="Net Banking">
                                    <Chip
                                      label="NB"
                                      size="small"
                                      color={row.emandate?.netBanking ? 'success' : 'default'}
                                      variant={row.emandate?.netBanking ? 'filled' : 'outlined'}
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.65rem',
                                        height: 20,
                                        minWidth: 36,
                                        '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                  </Tooltip>
                                  <Tooltip title="Debit Card">
                                    <Chip
                                      label="DC"
                                      size="small"
                                      color={row.emandate?.debitCard ? 'success' : 'default'}
                                      variant={row.emandate?.debitCard ? 'filled' : 'outlined'}
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.65rem',
                                        height: 20,
                                        minWidth: 36,
                                        '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                  </Tooltip>
                                  <Tooltip title="Aadhaar">
                                    <Chip
                                      label="AD"
                                      size="small"
                                      color={row.emandate?.aadhaar ? 'success' : 'default'}
                                      variant={row.emandate?.aadhaar ? 'filled' : 'outlined'}
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.65rem',
                                        height: 20,
                                        minWidth: 36,
                                        '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                  </Tooltip>
                                  <Tooltip title="PAN">
                                    <Chip
                                      label="PAN"
                                      size="small"
                                      color={row.emandate?.pan ? 'success' : 'default'}
                                      variant={row.emandate?.pan ? 'filled' : 'outlined'}
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.65rem',
                                        height: 20,
                                        minWidth: 36,
                                        '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                  </Tooltip>
                                </Box>
                                {showSettlement && (
                                  <Box
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(4, 36px)',
                                      gap: 0.5,
                                      mt: 0.25,
                                    }}
                                  >
                                    {([
                                      { code: 'NB', title: 'Net Banking Settlement', value: row.emandate?.netBankingSettlement },
                                      { code: 'DC', title: 'Debit Card Settlement', value: row.emandate?.debitCardSettlement },
                                      { code: 'AD', title: 'Aadhaar Settlement', value: row.emandate?.aadhaarSettlement },
                                      { code: 'PAN', title: 'PAN Settlement', value: row.emandate?.panSettlement },
                                    ] as const).map(({ code, title, value }) =>
                                      value ? (
                                        <Tooltip key={code} title={`${title}: ${value}`}>
                                          <Chip
                                            label={value}
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: '0.65rem',
                                              height: 20,
                                              fontFamily: 'monospace',
                                              '& .MuiChip-label': { px: 0.5 },
                                            }}
                                          />
                                        </Tooltip>
                                      ) : (
                                        <Box key={code} />
                                      )
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Tooltip title="View History">
                                  <IconButton
                                    size="small"
                                    onClick={() => openHistoryDialogForBank(row)}
                                    sx={{
                                      color: 'info.main',
                                      '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.1) }
                                    }}
                                  >
                                    <History fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {row.blacklistedMerchants &&
                                  row.blacklistedMerchants.length > 0 && (
                                    <Tooltip
                                      title={`View ${row.blacklistedMerchants.length} blacklisted merchant(s)`}
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={() => openBlacklistDialogForBank(row)}
                                        sx={{
                                          color: 'error.main',
                                          '&:hover': {
                                            bgcolor: alpha(theme.palette.error.main, 0.1),
                                          },
                                        }}
                                      >
                                        <Gavel fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        </Zoom>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Box sx={{ mt: 3, display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleDownload}
              sx={{
                flex: isMobile ? 1 : 'none',
                py: 1.5,
                px: 3,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                }
              }}
            >
              Download Excel
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => setOpenAddDialog(true)}
              sx={{
                flex: isMobile ? 1 : 'none',
                py: 1.5,
                px: 3,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                }
              }}
            >
              Add New Bank
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Gavel />}
              onClick={() => setOpenBlacklistManager(true)}
              sx={{
                flex: isMobile ? 1 : 'none',
                py: 1.5,
                px: 3,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)',
                }
              }}
            >
              Manage Blacklist
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={<Upload />}
              onClick={() => setOpenBulkSettlement(true)}
              sx={{
                flex: isMobile ? 1 : 'none',
                py: 1.5,
                px: 3,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(2, 136, 209, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(2, 136, 209, 0.4)',
                }
              }}
            >
              Bulk Upload Settlement Days
            </Button>
          </Box>

          <Card sx={{ mt: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Showing {Math.min((page - 1) * pageSize + 1, totalCount)} to{' '}
                  {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label="Rows per page"
                    select
                    SelectProps={{ native: true }}
                    size="small"
                    value={pageSize}
                    onChange={(e) =>
                      handlePageSizeChange(parseInt(e.target.value as unknown as string, 10))
                    }
                    sx={{
                      minWidth: 120,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  >
                    <option value={10}>10 rows</option>
                    <option value={20}>20 rows</option>
                    <option value={50}>50 rows</option>
                  </TextField>
                  <Pagination
                    count={Math.max(1, Math.ceil((totalCount || 0) / pageSize))}
                    page={page}
                    onChange={(_e, value) => handlePageChange(value)}
                    showFirstButton
                    showLastButton
                    size="medium"
                    color="primary"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Fade>

      {/* Add New Bank Dialog */}
      <BankFormDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        title="Add New Bank"
        onSubmit={handleAddBank}
        aggregator={aggregator}
        merchantId={merchantIdFilter.trim() || undefined}
        mode="add"
      />

      {/* Edit Bank Dialog */}
      <BankFormDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        title="Update Bank Details"
        onSubmit={handleUpdateBank}
        aggregator={aggregator}
        merchantId={merchantIdFilter.trim() || undefined}
        mode="edit"
        editingIndex={editingIndex}
        rows={rows}
        onOpenHistory={openHistoryDialogForBank}
      />

      {/* Bank History Dialog */}
      <BankHistoryDialog
        open={openHistoryDialog}
        onClose={() => setOpenHistoryDialog(false)}
        selectedBank={selectedBankForHistory}
        aggregator={aggregator}
      />

      {/* Bank Blacklist Dialog */}
      <BankBlacklistDialog
        open={openBlacklistDialog}
        onClose={() => setOpenBlacklistDialog(false)}
        bank={selectedBankForBlacklist}
        onEdit={(entry) => {
          setSelectedBlacklistEntry(entry);
          setOpenBlacklistDialog(false);
          setOpenBlacklistManager(true);
        }}
      />

      <BlacklistManager
        open={openBlacklistManager}
        onClose={() => {
          setOpenBlacklistManager(false);
          setSelectedBlacklistEntry(null);
        }}
        gateway={getGatewayParam(aggregator)}
        initialData={selectedBlacklistEntry}
        onSuccess={refreshData}
      />

      <BulkSettlementDaysDialog
        open={openBulkSettlement}
        onClose={() => setOpenBulkSettlement(false)}
        onSuccess={refreshData}
      />
    </Box>
  );
}
